import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { FileSelection, ItemPickerMode } from "../Documents/ItemPicker/ItemPicker";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import {
	getSharePointOpenActions,
	getWebDavOpenActions,
	retrieveSharepointModels,
	retrieveWebDavModels,
	SharePointOpenAction,
	WebDavOpenAction
} from "./documentsActions";
import { DocumentStoreType } from "client/Api/Documents/common";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { useEditorPreference } from "../Documents/useEditorPreference";
import { UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { isNil, isNotNil } from "client/Misc/utils";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { WebDavModels } from "client/Api/Documents/WebDav/webDav";
import { never } from "client_legacy/ndaw/errors";
import { useSelectFilesDialog } from "../Documents/Dialogs/SelectFilesDialog";
import { FormattedText } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { getFileNameWithoutExtension } from "client_legacy/Documents/path";
import { getExtensionAndType } from "client_legacy/Documents/fileExtensions";
import { LinkedDocumentFormData } from "./common";

export type AddDocumentsResult = {
	add: (() => Promise<void>) | null;
	openDocument: (item: LinkedDocumentFormData) => Promise<void>;
	dialogElement: ReactElement | null;
};

export type AddDocumentsOptions = {
	isSharepointEnabled: boolean;
	webDavConfiguration: WebDavConfiguration | null;
	maxItemsCount: number;
	items: readonly LinkedDocumentFormData[];
	onChangeIsLoading: (isLoading: boolean) => void;
	onSelect: (items: readonly LinkedDocumentFormData[]) => void;
};

export function useAddDocuments(options: AddDocumentsOptions): AddDocumentsResult {
	const {
		isSharepointEnabled,
		webDavConfiguration,
		items,
		maxItemsCount,
		onChangeIsLoading,
		onSelect
	} = options;

	const [selectFilesDialog, openSelectFilesDialog] = useSelectFilesDialog();
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const editorPreference: UserEditorPreference = useEditorPreference();

	const beforeSelectionMadeHandler = useCallback(
		(selection: FileSelection): Promise<boolean> => {
			const itemsToAdd: readonly LinkedDocumentFormData[] = convertSelectionToLinkedDocumentFormData(selection);
			const itemsToAddCount: number = itemsToAdd.length;
			const currentItemsCount: number = items.length;

			if (itemsToAddCount + currentItemsCount > maxItemsCount) {
				alertManager.showError(
					<FormattedText
						text={localize("_LinkedDocumentsMaxCountErrorMessage")}
						thingsToReplace={[
							{
								what: "{{MaxCount}}",
								filler: maxItemsCount
							}
						]}
					/>
				);

				return Promise.resolve(false);
			}

			const duplicatedItemNames: readonly string[] = getDuplicatedItemNames(items, itemsToAdd);

			if (duplicatedItemNames.length > 0) {
				alertManager.showError(
					<FormattedText
						text={localize("_LinkedDocumentsDuplicationErrorMessage")}
						thingsToReplace={[
							{
								what: "{{Names}}",
								filler: duplicatedItemNames.map((x, index) => index === 0
									? x
									: `, ${x}`
								)
							}
						]}
					/>
				);

				return Promise.resolve(false);
			}

			return Promise.resolve(true);
		},
		[
			alertManager,
			items,
			maxItemsCount
		]
	);

	const addHandler = useMemo(
		(): (() => Promise<void>) | null => {
			const isAnyStoreEnabled: boolean = isSharepointEnabled
				|| isNotNil(webDavConfiguration);

			if (!isAnyStoreEnabled) {
				return null;
			}

			return async (): Promise<void> => {
				const currentItemsCount: number = items.length;

				if (currentItemsCount >= maxItemsCount) {
					throw new Error(`The maximum number of allowed documents is ${maxItemsCount}.`);
				}

				const newFilesSelectionResult: TryResult<FileSelection> = await openSelectFilesDialog({
					onBeforeSelection: beforeSelectionMadeHandler,
					options: {
						sharePointOptions: isSharepointEnabled
							? {
								isEnabled: true,
								shouldShowPersonalViews: false,
								availableDestination: { kind: "all" }
							}
							: {
								isEnabled: false
							},
						webDavOptions: webDavConfiguration && webDavConfiguration.isConfigured
							? {
								isEnabled: true,
								availableDestination: { kind: "all" }
							}
							: {
								isEnabled: false
							},
						commonOptions: {
							mode: ItemPickerMode.MultipleFiles,
							creationOptions: {
								folder: true
							}
						}
					}
				});

				if (newFilesSelectionResult.wasSuccessful) {
					onSelect(
						convertSelectionToLinkedDocumentFormData(newFilesSelectionResult.result)
					);
				}
			};
		},
		[
			items.length,
			maxItemsCount,
			openSelectFilesDialog,
			beforeSelectionMadeHandler,
			isSharepointEnabled,
			webDavConfiguration,
			onSelect
		]
	);

	const openHandler = useCallback(
		async (item: LinkedDocumentFormData): Promise<void> => {

			switch (item.documentStoreType) {
				case DocumentStoreType.SharePoint:
					const {
						documentItem
					} = await retrieveSharepointModels(
						item.location,
						item.itemId,
						alertManager,
						onChangeIsLoading,
						globalCancellation
					);

					if (isNil(documentItem)) {
						throw new Error(`There is no data for the linked document with id '${item.itemId}'.`);
					}

					if (documentItem.itemType !== DocumentItemType.File) {
						throw new Error(`The linked document with id '${documentItem.id}' is not a file.`);
					}

					const sharePointActions: readonly SharePointOpenAction[] = getSharePointOpenActions(
						alertManager,
						editorPreference
					);

					sharePointActions.filter(x => x.canExecute(documentItem))[0]
						?.execute(documentItem);

					break;
				case DocumentStoreType.WebDav:
					if (!webDavConfiguration) {
						throw new Error("WebDav is not available");
					}

					const webDavModel: WebDavModels = await retrieveWebDavModels(
						item.serverId,
						item.fileId,
						alertManager,
						onChangeIsLoading,
						globalCancellation
					);

					if (webDavModel.item.isFolder) {
						throw new Error(`The linked document with id '${webDavModel.item.id}' is not a file.`);
					}

					const webDavActions: readonly WebDavOpenAction[] = getWebDavOpenActions(
						webDavModel.server,
						webDavConfiguration,
						alertManager,
						editorPreference
					);

					webDavActions.filter(x => x.canExecute(webDavModel.item))[0]
						?.execute(webDavModel.item);

					break;
				default:
					never(item);
			}
		},
		[
			alertManager,
			editorPreference,
			globalCancellation,
			onChangeIsLoading,
			webDavConfiguration
		]
	);

	return {
		add: addHandler,
		openDocument: openHandler,
		dialogElement: selectFilesDialog
	};
}

function convertSelectionToLinkedDocumentFormData(
	selection: FileSelection
): readonly LinkedDocumentFormData[] {
	switch (selection.kind) {
		case "sharepoint":
			return selection.items
				.map(x => ({
					documentStoreType: DocumentStoreType.SharePoint,
					itemId: x.id,
					location: x.location,
					name: getFileNameWithoutExtension(x.name),
					extension: x.extension
				}));
		case "webdav":
			return selection.items
				.map(x => ({
					documentStoreType: DocumentStoreType.WebDav,
					fileId: x.id,
					serverId: x.serverId,
					name: getFileNameWithoutExtension(x.name),
					extension: getExtensionAndType(x.name).extension
				}));
		default:
			never(selection);
	}
}

function getDuplicatedItemNames(
	currentItems: readonly LinkedDocumentFormData[],
	itemsToAdd: readonly LinkedDocumentFormData[]
): readonly string[] {
	const currentItemIds: readonly string[] = currentItems.map(x => getLinkedDocumentId(x));

	const result: string[] = itemsToAdd.reduce(
		(previousValue: string[], currentValue: LinkedDocumentFormData) => {
			const id: string = getLinkedDocumentId(currentValue);

			return currentItemIds.includes(id)
				? [...previousValue, currentValue.name]
				: previousValue;
		},
		[]
	);

	return result;
}

export function getLinkedDocumentId(
	item: LinkedDocumentFormData
): string {
	switch(item.documentStoreType) {
		case DocumentStoreType.SharePoint:
			return `${item.itemId}-${item.location.listId}-${item.location.siteUrl}`;
		case DocumentStoreType.WebDav:
			return `${item.fileId}-${item.serverId}`;
		default:
			never(item);
	}
}